import { useState } from "react";
import s from "./guide.module.css";
import { renderBlocks } from "./Blocks";
import { AiOutlook } from "./AiOutlook";

const Press = ({ on, ...p }) => <button type="button" aria-pressed={String(on)} {...p} />;

function Cases({ n, ctx }) {
  const [i, setI] = useState(0);
  const [title, copy] = ctx.w.cases[i];
  return (
    <>
      <div className={s.actions} aria-label={n.aria} role="group">
        {n.buttons.map((b, k) => <Press key={k} on={k === i} onClick={() => setI(k)} data-testid={`student-case-${k}`}>{b}</Press>)}
      </div>
      <div className={s.box} aria-live="polite" aria-atomic="true" data-testid="student-case-box"><h3>{title}</h3><p>{copy}</p></div>
      <details data-testid="student-cases-all">
        <summary>See all {ctx.w.cases.length} examples</summary>
        {ctx.w.cases.map(([t, c]) => <article key={t}><h3>{t}</h3><p>{c}</p></article>)}
      </details>
    </>
  );
}

function Reflect({ n, ctx }) {
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState("");
  const id = `student-thought-${ctx.slug}`;
  return (
    <div className={s.reflect} data-testid="student-reflect">
      <h3>{n.heading}</h3>
      <div className={s.actions} aria-label={n.aria} role="group">
        {n.buttons.map(([key, label]) => <Press key={key} on={pick === key} onClick={() => setPick(key)} data-testid={`student-reaction-${key.replace(/\s+/g, "-").toLowerCase()}`}>{label}</Press>)}
      </div>
      <p aria-live="polite" data-testid="student-reaction-text">{pick ? ctx.w.reactions[pick] : n.initial}</p>
      <label htmlFor={id}>{n.label}</label>
      <textarea id={id} rows={2} placeholder={n.placeholder} value={note} onChange={(e) => setNote(e.target.value)} data-testid="student-reflect-input" />
      <p className={s.source}>{n.note}</p>
    </div>
  );
}

function CountryCard({ name, ctx, side }) {
  const c = ctx.w.countries[name];
  const [l1, l2, l3] = ctx.w.country_fields;
  return (
    <div aria-live="polite" data-testid={`student-country-${side}-card`}>
      <h3>{name}</h3>
      <dl><dt>{l1}</dt><dd>{c.degree}</dd><dt>{l2}</dt><dd>{c.route}</dd><dt>{l3}</dt><dd>{c.question}</dd></dl>
      <details data-testid={`student-country-${side}-more`}>
        <summary>{ctx.w.country_details_label}</summary>
        {c.eligibility && <p data-testid={`student-country-${side}-eligibility`}><strong>Who can apply: </strong>{c.eligibility}</p>}
        <p>{c.detail}</p>
        <p className={s.source}>{c.source}</p>
        {c.link && <a href={c.link} target="_blank" rel="noopener noreferrer">{c.linkText}</a>}
      </details>
    </div>
  );
}

function AllCountries({ ctx }) {
  const [l1, l2, l3] = ctx.w.country_fields;
  const names = Object.keys(ctx.w.countries);
  return (
    <details data-testid="student-countries-all">
      <summary>See all {names.length} countries at a glance</summary>
      {names.map((name) => {
        const c = ctx.w.countries[name];
        return (
          <article key={name} className={s.task}>
            <h3>{name}</h3>
            <dl><dt>{l1}</dt><dd>{c.degree}</dd><dt>{l2}</dt><dd>{c.route}</dd><dt>{l3}</dt><dd>{c.question}</dd></dl>
            {c.eligibility && <p><strong>Who can apply: </strong>{c.eligibility}</p>}
            <p>{c.detail}</p>
            <p className={s.source}>{c.source}</p>
            {c.link && <a href={c.link} target="_blank" rel="noopener noreferrer">{c.linkText}</a>}
          </article>
        );
      })}
    </details>
  );
}

function Compare({ n, ctx }) {
  const [sel, setSel] = useState(ctx.w.country_defaults);
  const names = Object.keys(ctx.w.countries);
  return (
    <>
    <div className={s.grid}>
      {["a", "b"].map((side, k) => (
        <article key={side} className={s.country}>
          <label htmlFor={`student-country-${ctx.slug}-${side}`}>{n.labels[k]}
            <select id={`student-country-${ctx.slug}-${side}`} value={sel[k]} data-testid={`student-country-${side}`}
              onChange={(e) => setSel(k === 0 ? [e.target.value, sel[1]] : [sel[0], e.target.value])}>
              {names.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <CountryCard name={sel[k]} ctx={ctx} side={side} />
        </article>
      ))}
    </div>
    <AllCountries ctx={ctx} />
    </>
  );
}

function Careers({ n, ctx }) {
  const [i, setI] = useState(0);
  const [title, work, route] = ctx.w.careers[i];
  return (
    <>
      <AiOutlook ai={ctx.ai} />
      <div className={s.actions} aria-label={n.aria} role="group">
        {ctx.w.careers.map((c, k) => <Press key={k} on={k === i} onClick={() => setI(k)} data-testid={`student-career-${k}`}>{c[0]}</Press>)}
      </div>
      <div className={s.box} aria-live="polite" aria-atomic="true" data-testid="student-career-box"><h3>{title}</h3><p>{work}</p><p>{route}</p></div>
      {ctx.w.career_exception && <p className={s.note} data-testid="student-career-exception">{ctx.w.career_exception}</p>}
      <details data-testid="student-careers-all">
        <summary>See all {ctx.w.careers.length} career directions</summary>
        {ctx.w.careers.map(([t, w, r]) => <article key={t} className={s.task}><h3>{t}</h3><p>{w}</p><p>{r}</p></article>)}
      </details>
    </>
  );
}

function Budget({ n, ctx }) {
  const b = ctx.w.budget;
  const [vals, setVals] = useState({});
  const [msg, setMsg] = useState(n.initial);
  const valid = (f, v) => {
    if (v === undefined || v === "" || !Number.isFinite(Number(v))) return false;
    const x = Number(v);
    if (f.min != null && x < Number(f.min)) return false;
    if (f.max != null && x > Number(f.max)) return false;
    return f.step !== "1" || Number.isInteger(x);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!n.fields.every((f) => valid(f, vals[f.key]))) return setMsg(b.invalid);
    const { tuition, living, years, other } = Object.fromEntries(n.fields.map((f) => [f.key, Number(vals[f.key])]));
    const total = (tuition + living) * years + other;
    setMsg(b.result_prefix + total.toLocaleString(undefined, { maximumFractionDigits: 2 }) + b.result_suffix);
  };
  return (
    <form className={s.form} onSubmit={submit} noValidate data-testid="student-budget">
      <p>{renderBlocks(n.intro, ctx)}</p>
      <div className={s.grid}>
        {n.fields.map((f) => (
          <label key={f.key}>{f.label}
            <input type="number" min={f.min ?? undefined} max={f.max ?? undefined} step={f.step} required inputMode={f.inputmode}
              placeholder={f.placeholder} value={vals[f.key] ?? ""} data-testid={`student-budget-${f.key}`}
              onChange={(e) => { setVals({ ...vals, [f.key]: e.target.value }); setMsg(b.changed); }} />
          </label>
        ))}
      </div>
      <button type="submit" data-testid="student-budget-submit">{n.button}</button>
      <p className={s.result} aria-live="polite" data-testid="student-budget-result">{msg}</p>
    </form>
  );
}

const Goto = ({ n, ctx }) => (
  <button type="button" className={[s.goto, n.primary && s.primary].filter(Boolean).join(" ")} onClick={() => ctx.go(n.target, true)} data-testid={`student-goto-${n.target}`}>{n.label}</button>
);

const MAP = { cases: Cases, reflect: Reflect, compare: Compare, careers: Careers, budget: Budget, goto: Goto };

export function Widget({ n, ctx }) {
  const W = MAP[n.w];
  return W ? <W n={n} ctx={ctx} /> : null;
}
